import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'
import pull from 'lodash-es/pull'
import axios from '/components/configs/axios'
import { useAuthContext } from './authProvider'

const safeLocalStorage = {
  getItem: (key) => {
    try {
      return typeof window !== 'undefined' ? localStorage.getItem(key) : null
    } catch {
      return null
    }
  },
  setItem: (key, value) => {
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem(key, value)
      }
    } catch {
      // localStorage not available (e.g., Safari private browsing)
    }
  }
}

const FavouritesContext = createContext('default')

const useFavouritesContext = () => {
  const context = useContext(FavouritesContext)

  if (context === undefined) {
    throw new Error('useFavouritesContext was used outside of its Provider')
  }

  return context
}

const FavouritesContextProvider = ({ children }) => {
  const [favourites, setFavourites] = useState([])
  const { user, loggedIn } = useAuthContext()
  const hasInitializedRef = useRef(false)

  function isFavourite(value) {
    return favourites.includes(value)
  }

  function addOrRemoveFavourite(value) {
    let newFavourites = [...favourites]

    if (isFavourite(value)) {
      pull(newFavourites, value)
    } else {
      newFavourites.push(value)
    }

    setFavourites(newFavourites)
    safeLocalStorage.setItem('favourites', newFavourites.join(','))

    if (loggedIn) {
      axios
        .post('user/favourites/update', {
          user_id: user,
          favourites: newFavourites
        })
        .catch((err) => console.error(err))
    }
  }

  // Load favourites from localStorage on initial client-side mount
  useEffect(() => {
    const storedFavourites = safeLocalStorage.getItem('favourites')
    if (storedFavourites && storedFavourites !== '') {
      setFavourites(storedFavourites.split(',').filter(Boolean))
    }
    hasInitializedRef.current = true
  }, [])

  // Sync with database when user logs in
  useEffect(() => {
    if (!loggedIn || !hasInitializedRef.current) {
      return
    }

    // Get current local favourites before async operation
    const localFavourites = safeLocalStorage.getItem('favourites')
    const localIds = localFavourites
      ? localFavourites.split(',').filter(Boolean)
      : []

    axios
      .get(`user/${user}/favourites`)
      .then((res) => {
        const dbFavourites = Array.isArray(res.data.favourites)
          ? res.data.favourites
          : []

        // Merge local and DB favourites (union of both)
        const merged = [...new Set([...localIds, ...dbFavourites])]

        setFavourites(merged)
        safeLocalStorage.setItem('favourites', merged.join(','))

        // Save merged result back to DB if there were local additions
        if (localIds.length > 0 && merged.length > dbFavourites.length) {
          axios
            .post('user/favourites/update', {
              user_id: user,
              favourites: merged
            })
            .catch((err) => console.error(err))
        }
      })
      .catch((err) => console.error(err))
  }, [loggedIn, user])

  return (
    <FavouritesContext.Provider
      value={{
        favourites,
        isFavourite,
        addOrRemoveFavourite
      }}
    >
      {children}
    </FavouritesContext.Provider>
  )
}

export { useFavouritesContext, FavouritesContext, FavouritesContextProvider }
